import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { testLabGetFeedback, testLabSaveFeedback } from '../../db/test-lab-resolvers.js';

type Feedback = {
  sessionID: string;
  testerName: string;
  testerNotes: string;
  testerNotesUpdatedAt: string | null;
};
type RequestState = { alive: boolean; revision: number; saving: boolean };

function parseFeedback(raw: unknown): Feedback {
  const value = typeof raw === 'string' ? JSON.parse(raw) : null;
  if (
    !value ||
    typeof value.sessionID !== 'string' ||
    !value.sessionID.trim() ||
    (value.testerName !== null && typeof value.testerName !== 'string') ||
    (value.testerNotes !== null && typeof value.testerNotes !== 'string') ||
    (value.testerNotesUpdatedAt !== null && typeof value.testerNotesUpdatedAt !== 'string')
  )
    throw new Error('Invalid feedback response');
  return {
    sessionID: value.sessionID,
    testerName: value.testerName ?? '',
    testerNotes: value.testerNotes ?? '',
    testerNotesUpdatedAt: value.testerNotesUpdatedAt,
  };
}
function feedbackDate(value: string | null) {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime())
    ? date.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
    : null;
}

/** One feedback record per call. Never expose an empty editor before the existing record was read. */
export const CallFeedbackPanel = stateful(({ callID }: { callID: string }) => {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading', [callID]);
  const [name, setName] = useState('', [callID]);
  const [notes, setNotes] = useState('', [callID]);
  const [saved, setSaved] = useState<Feedback | null>(null, [callID]);
  const [saving, setSaving] = useState(false, [callID]);
  const [saveConfirmed, setSaveConfirmed] = useState(false, [callID]);
  const [error, setError] = useState<string | null>(null, [callID]);
  const [request] = useState<RequestState>({ alive: true, revision: 0, saving: false }, [callID]);

  const load = async () => {
    if (request.saving) return;
    const revision = ++request.revision;
    setStatus('loading');
    setError(null);
    try {
      if (!callID) throw new Error('Missing call ID');
      const feedback = parseFeedback(await testLabGetFeedback(callID)());
      if (!request.alive || revision !== request.revision) return;
      setName(feedback.testerName);
      setNotes(feedback.testerNotes);
      setSaved(feedback);
      setSaveConfirmed(false);
      setStatus('ready');
    } catch (cause) {
      if (!request.alive || revision !== request.revision) return;
      setError(
        cause instanceof Error && cause.message.includes('FEEDBACK_NOT_FOUND')
          ? 'Für diesen Anruf ist kein bearbeitbarer Feedback-Eintrag verfügbar.'
          : 'Feedback zu diesem Anruf konnte nicht geladen werden. Bitte erneut versuchen.',
      );
      setStatus('error');
    }
  };
  useEffect(() => {
    request.alive = true;
    void load();
    return () => {
      request.alive = false;
      ++request.revision;
    };
  }, [callID]);

  const save = async () => {
    if (!request.alive || request.saving || status !== 'ready' || !saved) return;
    const nextName = name.trim(),
      nextNotes = notes.trim();
    if (!nextName || !nextNotes || nextName.length > 100 || nextNotes.length > 4000) {
      setError('Bitte einen Namen (bis 100 Zeichen) und Beobachtungen (bis 4.000 Zeichen) eingeben.');
      return;
    }
    request.saving = true;
    const revision = ++request.revision;
    setSaving(true);
    setError(null);
    setSaveConfirmed(false);
    try {
      await testLabSaveFeedback(saved.sessionID, nextName, nextNotes)();
      if (!request.alive || revision !== request.revision) return;
      setSaved({
        sessionID: saved.sessionID,
        testerName: nextName,
        testerNotes: nextNotes,
        testerNotesUpdatedAt: null,
      });
      setName(nextName);
      setNotes(nextNotes);
      setSaveConfirmed(true);
    } catch {
      if (request.alive && revision === request.revision)
        setError('Feedback konnte nicht gespeichert werden. Ihre Eingaben bleiben erhalten. Bitte erneut versuchen.');
    } finally {
      request.saving = false;
      if (request.alive && revision === request.revision) setSaving(false);
    }
  };

  const hasFeedback = !!saved && (!!saved.testerName || !!saved.testerNotes);
  const unchanged = !!saved && name.trim() === saved.testerName && notes.trim() === saved.testerNotes;
  const confirmed = hasFeedback && unchanged && status === 'ready';
  const stamp = feedbackDate(saved?.testerNotesUpdatedAt ?? null);

  return html`
    <style>
      .zw-call-feedback {
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
        padding: 24px;
        border: 1px solid #e1e6ed;
        border-radius: 14px;
        background: #fff;
        color: #344054;
      }
      .zw-call-feedback * {
        box-sizing: border-box;
      }
      .zw-call-feedback :is(h3, p) {
        margin: 0;
      }
      .zw-call-feedback .zw-cf-heading {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 9px;
      }
      .zw-call-feedback .zw-cf-eyebrow {
        margin-bottom: 7px;
        color: #d6001c;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.13em;
        text-transform: uppercase;
      }
      .zw-call-feedback h3 {
        font-size: 19px;
        font-weight: 750;
        letter-spacing: -0.025em;
        line-height: 1.35;
      }
      .zw-call-feedback .zw-cf-badge {
        padding: 4px 8px;
        border-radius: 6px;
        background: #edf8f1;
        color: #24764b;
        font-size: 10px;
        font-weight: 650;
      }
      .zw-call-feedback .zw-cf-intro {
        margin-top: 10px;
        color: #7a8597;
        font-size: 12px;
        line-height: 1.75;
      }
      .zw-call-feedback .zw-cf-id {
        display: block;
        margin-top: 11px;
        color: #98a2b3;
        font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
        font-size: 10px;
        line-height: 1.6;
        overflow-wrap: anywhere;
      }
      .zw-call-feedback .zw-cf-form {
        margin-top: 23px;
        display: flex;
        flex-direction: column;
        gap: 18px;
      }
      .zw-call-feedback .zw-cf-field {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;
      }
      .zw-call-feedback .zw-cf-field-name {
        max-width: 320px;
      }
      .zw-call-feedback label {
        color: #526078;
        font-size: 11px;
        font-weight: 650;
      }
      .zw-call-feedback .zw-cf-input,
      .zw-call-feedback .zw-cf-textarea {
        width: 100%;
        min-width: 0;
        padding: 11px 13px;
        border: 1px solid #d4dce6;
        border-radius: 8px;
        background: #fff;
        color: #344054;
        font-family: inherit;
        font-size: 13px;
        line-height: 1.75;
      }
      .zw-call-feedback .zw-cf-textarea {
        min-height: 145px;
        resize: vertical;
      }
      .zw-call-feedback :is(input, textarea)::placeholder {
        color: #98a2b3;
      }
      .zw-call-feedback .zw-cf-note-footer {
        display: flex;
        justify-content: space-between;
        gap: 14px;
        align-items: flex-start;
        color: #8a94a5;
        font-size: 10px;
        line-height: 1.7;
      }
      .zw-call-feedback .zw-cf-counter {
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
      .zw-call-feedback .zw-cf-actions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px 17px;
      }
      .zw-call-feedback .zw-cf-button {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        min-height: 40px;
        padding: 10px 14px;
        border: 1px solid #d6001c;
        border-radius: 8px;
        background: #d6001c;
        color: #fff;
        font-family: inherit;
        font-size: 12px;
        font-weight: 650;
        line-height: 1.5;
        cursor: pointer;
      }
      .zw-call-feedback .zw-cf-button:hover:not(:disabled) {
        border-color: #b60018;
        background: #b60018;
      }
      .zw-call-feedback .zw-cf-button:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
      .zw-call-feedback .zw-cf-link {
        color: #738097;
        font-size: 11px;
        text-decoration: none;
        line-height: 1.7;
      }
      .zw-call-feedback .zw-cf-link:hover {
        color: #d6001c;
        text-decoration: underline;
        text-underline-offset: 3px;
      }
      .zw-call-feedback .zw-cf-message {
        margin-top: 17px;
        color: #7a8597;
        font-size: 12px;
        line-height: 1.75;
      }
      .zw-call-feedback .zw-cf-error {
        padding: 12px 14px;
        border: 1px solid #f0c9cf;
        border-radius: 8px;
        background: #fff6f7;
        color: #a6223b;
      }
      .zw-call-feedback .zw-cf-success {
        color: #24764b;
      }
      .zw-call-feedback .zw-cf-retry {
        margin-top: 13px;
      }
      .zw-call-feedback :is(button, a, input, textarea):focus-visible {
        outline: 3px solid #ecc3cb;
        outline-offset: 3px;
      }
      @media (max-width: 640px) {
        .zw-call-feedback {
          padding: 20px;
        }
        .zw-call-feedback .zw-cf-field-name {
          max-width: none;
        }
        .zw-call-feedback .zw-cf-note-footer {
          flex-wrap: wrap;
          gap: 5px;
        }
        .zw-call-feedback .zw-cf-counter {
          margin-left: auto;
        }
      }
    </style>
    <section
      class="zw-call-feedback"
      aria-label="Feedback zu diesem Anruf"
      aria-busy=${status === 'loading' || saving ? 'true' : 'false'}
    >
      <p class="zw-cf-eyebrow">Ihre Beobachtungen</p>
      <div class="zw-cf-heading">
        <h3>Feedback zum Anruf</h3>
        ${confirmed ? html`<span class="zw-cf-badge">Gespeichert</span>` : nothing}
      </div>
      <p class="zw-cf-intro">
        Was ist Ihnen aufgefallen? Halten Sie Ihre Beobachtungen zur Gesprächsführung, Sprachqualität oder zu einzelnen
        Schritten fest.
      </p>
      <span class="zw-cf-id" title=${callID}>Anruf · ${callID}</span>
      ${status === 'loading'
        ? html`<p class="zw-cf-message" role="status">Vorhandenes Feedback wird geladen…</p>`
        : status === 'error'
          ? html`
              <p class="zw-cf-message zw-cf-error" role="alert">${error}</p>
              <button type="button" class="zw-cf-button zw-cf-retry" @click=${() => void load()}>Erneut laden</button>
            `
          : html`
              <div class="zw-cf-form">
                <div class="zw-cf-field zw-cf-field-name">
                  <label for="feedback-name">Name</label>
                  <input
                    id="feedback-name"
                    class="zw-cf-input"
                    type="text"
                    placeholder="Ihr Name"
                    autocomplete="name"
                    maxlength="100"
                    .value=${name}
                    @input=${(event: Event) => setName((event.target as HTMLInputElement).value)}
                    ?disabled=${saving}
                  />
                </div>
                <div class="zw-cf-field">
                  <label for="feedback-notes">Beobachtungen (DE / EN)</label>
                  <textarea
                    id="feedback-notes"
                    class="zw-cf-textarea"
                    rows="5"
                    maxlength="4000"
                    placeholder="Was hat gut funktioniert? Wo gab es Schwierigkeiten?"
                    .value=${notes}
                    @input=${(event: Event) => setNotes((event.target as HTMLTextAreaElement).value)}
                    ?disabled=${saving}
                  ></textarea>
                  <div class="zw-cf-note-footer">
                    <span
                      >${hasFeedback
                        ? 'Änderungen aktualisieren das bestehende Feedback zu diesem Anruf.'
                        : 'Ihr Feedback wird diesem Anruf zugeordnet.'}</span
                    ><span class="zw-cf-counter">${notes.length} / 4.000</span>
                  </div>
                </div>
                <div class="zw-cf-actions">
                  <button
                    type="button"
                    class="zw-cf-button"
                    @click=${save}
                    ?disabled=${saving ||
                    !name.trim() ||
                    !notes.trim() ||
                    name.trim().length > 100 ||
                    notes.trim().length > 4000 ||
                    confirmed}
                  >
                    ${saving ? 'Speichere…' : hasFeedback ? 'Änderungen speichern' : 'Feedback speichern'}
                  </button>
                  <a class="zw-cf-link" href="/test-lab/feedback">Alle Feedback-Einträge ansehen ↗</a>
                </div>
              </div>
              ${error ? html`<p class="zw-cf-message zw-cf-error" role="alert">${error}</p>` : nothing}
              ${saveConfirmed && unchanged
                ? html`<p class="zw-cf-message zw-cf-success" role="status">
                    Feedback gespeichert. Sie können es hier jederzeit bearbeiten.
                  </p>`
                : confirmed && stamp
                  ? html`<p class="zw-cf-message">Zuletzt gespeichert: ${stamp}</p>`
                  : nothing}
            `}
    </section>
  `;
});
